import "./index.css";
import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "./i18n/config";
import { FEATURE_FLAGS } from "./config/featureFlags";
import { containsThai } from "./utils/localization";
import Landing from "./app/landing_module";
import PageNotAvailable from "./component/common/PageNotAvailable";
import ScrollToTop from "./component/common/ScrollToTop";
import CookieConsent from "./component/common/CookieConsent";

// Lazy load page components
const LiveAndInvest = lazy(() => import("./component/pages/LiveAndInvest"));
const ReadOurStory = lazy(() => import("./component/pages/ReadOurStory"));
const LearnMoreNews = lazy(() => import("./component/pages/LearnMoreNews"));
const ViewAllProjects = lazy(() => import("./component/pages/ViewAllProjects"));
const ContactUs = lazy(() => import("./component/pages/ContactUs"));
const Career = lazy(() => import("./component/pages/Career"));
const CareerDetail = lazy(() => import("./component/pages/CareerDetail"));
const LivingStandardsDetail = lazy(() => import("./component/pages/LivingStandards"));
const ProjectDetails = lazy(() => import("./component/pages/ProjectDetails"));
const NewsBlogDetail = lazy(() => import("./component/pages/NewsBlogDetail"));
const BasicInfomationPoject = lazy(() => import("./component/pages/BasicInfomationPoject"));
const Promotion = lazy(() => import("./component/pages/Promotion"));
const PromotionDetail = lazy(() => import("./component/pages/PromotionDetail"));

const LoadingFallback = () => (
  <div className="min-h-screen bg-white">
    <div className="pt-12 sm:pt-14 md:pt-16">
      <div className="space-y-8">
        <div className="h-64 bg-white animate-pulse" />
        <div className="h-96 bg-white animate-pulse" />
        <div className="h-64 bg-white animate-pulse" />
      </div>
    </div>
  </div>
);

function AppRoutes() {
  const { t } = useTranslation();
  
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route
        path="/live-or-invest"
        element={
          FEATURE_FLAGS.LIVE_AND_INVEST ? (
            <Suspense fallback={<LoadingFallback />}>
              <LiveAndInvest />
            </Suspense>
          ) : (
            <PageNotAvailable pageName={t("pageNotAvailable.liveAndInvest")} />
          )
        }
      />
      <Route
        path="/read-our-story"
        element={
          FEATURE_FLAGS.READ_OUR_STORY ? (
            <Suspense fallback={<LoadingFallback />}>
              <ReadOurStory />
            </Suspense>
          ) : (
            <PageNotAvailable pageName={t("pageNotAvailable.readOurStory")} />
          )
        }
      />
      <Route
        path="/learn-more"
        element={
          FEATURE_FLAGS.LEARN_MORE ? (
            <Suspense fallback={<LoadingFallback />}>
              <LearnMoreNews />
            </Suspense>
          ) : (
            <PageNotAvailable pageName={t("pageNotAvailable.newsPromotions")} />
          )
        }
      />
      <Route
        path="/view-all-projects"
        element={
          FEATURE_FLAGS.VIEW_ALL_PROJECTS ? (
            <Suspense fallback={<LoadingFallback />}>
              <ViewAllProjects />
            </Suspense>
          ) : (
            <PageNotAvailable pageName={t("pageNotAvailable.viewAllProjects")} />
          )
        }
      />
      <Route
        path="/contact-us"
        element={
          FEATURE_FLAGS.CONTACT_US ? (
            <Suspense fallback={<LoadingFallback />}>
              <ContactUs />
            </Suspense>
          ) : (
            <PageNotAvailable pageName={t("pageNotAvailable.contactUs")} />
          )
        }
      />
      <Route
        path="/career"
        element={
          FEATURE_FLAGS.CAREER ? (
            <Suspense fallback={<LoadingFallback />}>
              <Career />
            </Suspense>
          ) : (
            <PageNotAvailable pageName={t("pageNotAvailable.career")} />
          )
        }
      />
      <Route
        path="/career/:uuid"
        element={
          FEATURE_FLAGS.CAREER ? (
            <Suspense fallback={<LoadingFallback />}>
              <CareerDetail />
            </Suspense>
          ) : (
            <PageNotAvailable pageName={t("pageNotAvailable.careerDetail")} />
          )
        }
      />
      <Route
        path="/living-standards/:id"
        element={
          <Suspense fallback={<LoadingFallback />}>
            <LivingStandardsDetail />
          </Suspense>
        }
      />
      <Route
        path="/projects/:uuid"
        element={
          <Suspense fallback={<LoadingFallback />}>
            <ProjectDetails />
          </Suspense>
        }
      />
      <Route
        path="/news/blogs/:uuid"
        element={
          <Suspense fallback={<LoadingFallback />}>
            <NewsBlogDetail />
          </Suspense>
        }
      />
      <Route
        path="/basic-information-project/:uuid"
        element={
          <Suspense fallback={<LoadingFallback />}>
            <BasicInfomationPoject />
          </Suspense>
        }
      />
      <Route
        path="/promotion"
        element={
          <Suspense fallback={<LoadingFallback />}>
            <Promotion />
          </Suspense>
        }
      />
      <Route
        path="/promotion/:uuid"
        element={
          <Suspense fallback={<LoadingFallback />}>
            <PromotionDetail />
          </Suspense>
        }
      />
    </Routes>
  );
}

function App() {
  // Update HTML lang attribute based on i18n language
  useEffect(() => {
    const updateLangAttribute = () => {
      const htmlElement = document.documentElement;
      const currentLang = i18n.language || 'en';
      htmlElement.setAttribute('lang', currentLang);
    };

    // Set initial lang attribute
    updateLangAttribute();

    // Listen for language changes
    i18n.on('languageChanged', updateLangAttribute);

    return () => {
      i18n.off('languageChanged', updateLangAttribute);
    };
  }, []);

  // Add font-thai class ONLY to elements containing Thai text
  // This ensures English text keeps its original font
  useEffect(() => {
    const processElement = (element) => {
      if (!element || element.nodeType !== Node.ELEMENT_NODE) return;
      if (element.dataset.thaiFontProcessed === 'true') return;

      // Check if element has direct text nodes (not from children) with Thai characters
      let directThaiTextNodes = [];
      for (let i = 0; i < element.childNodes.length; i++) {
        const node = element.childNodes[i];
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent || '';
          if (containsThai(text.trim())) {
            directThaiTextNodes.push(node);
          }
        }
      }

      // Only add class if element has direct Thai text nodes
      // AND element has no child elements (to avoid affecting parent containers)
      if (directThaiTextNodes.length > 0 && element.children.length === 0) {
        element.classList.add('font-thai');
        element.dataset.thaiFontProcessed = 'true';
      }

      // Process child elements first (bottom-up approach)
      Array.from(element.children).forEach((child) => {
        if (child.dataset.thaiFontProcessed !== 'true') {
          processElement(child);
        }
      });
    };

    // Process all elements in DOM
    const processDOM = () => {
      if (document.body) {
        const allElements = document.body.querySelectorAll('*');
        // Process in reverse order (children first, then parents)
        Array.from(allElements).reverse().forEach((el) => {
          if (el.dataset.thaiFontProcessed !== 'true') {
            processElement(el);
          }
        });
      }
    };

    // Wait for DOM to be ready
    if (document.body) {
      processDOM();
    } else {
      const checkBody = setInterval(() => {
        if (document.body) {
          clearInterval(checkBody);
          processDOM();
        }
      }, 100);
    }

    // Use MutationObserver to watch for DOM changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            processElement(node);
          } else if (node.nodeType === Node.TEXT_NODE && node.parentElement) {
            const parent = node.parentElement;
            if (parent && parent.dataset.thaiFontProcessed !== 'true') {
              processElement(parent);
            }
          }
        });
      });
    });

    // Start observing
    if (document.body) {
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <CookieConsent />
      <AppRoutes />
    </BrowserRouter>
  );
}

export default App;
